import { Close } from "@mui/icons-material";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  Divider,
  InputBase,
  MenuItem,
  Radio,
  Snackbar,
  Tooltip,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import timezones from "timezones-list";
import TimezoneSelect from "react-timezone-select";
import ApiKeyModal from "../modals/GenerateApiKeyModal";
import RouteSelect from "../reusables/RouteSelect";
import {
  getNotificationChannelOptions,
  normalizeOutboundNotificationConfigForApi,
  readOutboundNotificationConfig,
} from "../../utils/outboundNotificationSettings";

const Organization = () => {
  const [org_data, setOrgData] = useState(
    JSON.parse(localStorage.getItem("organization"))
  );
  const [loader1, setLoader1] = useState(false);
  const [errorMsg, setErrormsg] = useState(null);
  const [loader, setLoader] = useState(false);
  const [showRouteConflictModal, setShowRouteConflictModal] = useState(false);
  const [affectedRoutes, setAffectedRoutes] = useState([]);
  const [routeConflictAction, setRouteConflictAction] = useState(null); // 'disable' or 'enable'
  const [pendingOrgUpdate, setPendingOrgUpdate] = useState(null);

  const [organization, setOrganization] = useState(
    // Sample Data
    {
      orgId: org_data.orgId.S,
      organization_name: org_data.organization_name.S,
      org_timezone: org_data.org_timezone.S,
      twilio_integration: {
        account_sid: org_data.twilio_integration.M.account_sid.S,

        app_sid: org_data.twilio_integration?.M?.app_sid?.S,
        api_sid: org_data.twilio_integration?.M?.api_sid?.S,
        api_secret: org_data.twilio_integration?.M?.api_secret?.S,

        api_key: "",
        transcription_enabled:
          org_data.twilio_integration.M.transcription_enabled.BOOL,
        auto_configuration:
          org_data.twilio_integration.M.auto_configuration.BOOL,
        primary_phone: org_data.twilio_integration.M.primary_phone.S,
      },
      recording_transcription_config: {
        inbound_recording: org_data.recording_transcription_config?.M?.inbound_recording?.S || "disallow",
        inbound_transcription: org_data.recording_transcription_config?.M?.inbound_transcription?.S || "disallow",
        outbound_recording: org_data.recording_transcription_config?.M?.outbound_recording?.S || "disallow",
        outbound_transcription: org_data.recording_transcription_config?.M?.outbound_transcription?.S || "disallow",
      },
      outbound_notification_config: (() => {
        const { channel_id, policy } = readOutboundNotificationConfig(org_data);
        return {
          channel_id,
          policy: channel_id ? policy || "allow" : null,
        };
      })(),
    }
  );

  const notificationChannelOptions = React.useMemo(() => {
    const options = getNotificationChannelOptions(org_data?.channels);
    const savedId = organization.outbound_notification_config?.channel_id;
    if (
      savedId &&
      !options.some((opt) => opt.value === savedId)
    ) {
      return [
        {
          value: savedId,
          label: "Channel no longer available",
          disabled: true,
        },
        ...options,
      ];
    }
    return options;
  }, [org_data?.channels, organization.outbound_notification_config?.channel_id]);

  const [successMsg, setSuccessMsg] = useState(null);

  /** Convert DynamoDB AttributeValue format to plain JSON (for update-route API). */
  function parseDynamoItem(item) {
    if (!item || typeof item !== "object") return item;
    if (item.S !== undefined) return item.S;
    if (item.N !== undefined) return Number(item.N);
    if (item.BOOL !== undefined) return item.BOOL;
    if (item.NULL) return null;
    if (item.L) return item.L.map(parseDynamoItem);
    if (item.M) {
      const result = {};
      for (const [key, value] of Object.entries(item.M)) {
        result[key] = parseDynamoItem(value);
      }
      return result;
    }
    // Plain JS arrays must stay arrays (Object.entries would yield { "0": … }).
    if (Array.isArray(item)) {
      return item.map(parseDynamoItem);
    }
    const result = {};
    for (const [key, value] of Object.entries(item)) {
      result[key] = parseDynamoItem(value);
    }
    return result;
  }

  async function checkAffectedRoutes(newConfig) {
    try {
      // Get current organization policy
      const currentConfig = org_data.recording_transcription_config?.M || {};
      const currentInboundRecording = currentConfig.inbound_recording?.S || "disallow";
      
      // Fetch all routes for this organization
      const routesResponse = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "route/get-routes",
        { org_id: org_data.orgId.S }
      );
      
      // Check if we're changing inbound recording to "disallow"
      const isDisablingInboundRecording = 
        currentInboundRecording !== "disallow" && 
        newConfig.inbound_recording === "disallow";
      
      // Check if we're changing inbound recording to "require"
      const isRequiringInboundRecording = 
        currentInboundRecording !== "require" && 
        newConfig.inbound_recording === "require";
      
      if (isDisablingInboundRecording) {
        // Find routes with recording enabled
        const routesWithRecording = routesResponse.data.routes.filter(route => {
          const recordCall = route.recordCall?.BOOL !== undefined
            ? route.recordCall.BOOL
            : route.recordCall === true;
          return recordCall === true;
        });
        
        return { routes: routesWithRecording, action: 'disable' };
      } else if (isRequiringInboundRecording) {
        // Find routes without recording enabled
        const routesWithoutRecording = routesResponse.data.routes.filter(route => {
          const recordCall = route.recordCall?.BOOL !== undefined
            ? route.recordCall.BOOL
            : route.recordCall === true;
          return recordCall !== true;
        });
        
        return { routes: routesWithoutRecording, action: 'enable' };
      }
      
      return { routes: [], action: null };
    } catch (error) {
      // Log for production diagnostics when route check fails
      console.error("Error checking affected routes:", error);
      return { routes: [], action: null };
    }
  }

  async function updateAffectedRoutes(routes, action) {
    try {
      const orgId = org_data?.orgId?.S || JSON.parse(localStorage.getItem("organization"))?.orgId?.S;
      // Update each route based on the action
      for (const route of routes) {
        // get-routes returns DynamoDB format; update-route expects plain JSON (same as route config page)
        const routePlain = parseDynamoItem(route);
        const routeToUpdate = { ...routePlain };

        if (action === "disable") {
          routeToUpdate.recordCall = false;
          routeToUpdate.transcribeCall = false;
        } else if (action === "enable") {
          routeToUpdate.recordCall = true;
          const newConfig = organization.recording_transcription_config;
          if (newConfig.inbound_transcription === "require") {
            routeToUpdate.transcribeCall = true;
          }
        }

        await axios.post(
          process.env.REACT_APP_BACKEND_URL + "route/update-route",
          { ...routeToUpdate, org_id: orgId }
        );
      }
      return true;
    } catch (error) {
      console.error("Error updating routes:", error);
      return false;
    }
  }

  async function updateOrganization() {
    setErrormsg(null);

    // Validation: Inbound - Cannot have transcription enabled if call recording is disallowed
    if (organization.recording_transcription_config.inbound_transcription !== "disallow" && organization.recording_transcription_config.inbound_recording === "disallow") {
      setErrormsg("Inbound transcription requires call recording to be enabled. Please enable inbound call recording first.");
      setLoader(false);
      return;
    }

    // Validation: Inbound - Cannot have transcription required if call recording is not required
    if (organization.recording_transcription_config.inbound_transcription === "require" && organization.recording_transcription_config.inbound_recording !== "require") {
      setErrormsg("Inbound transcription cannot be required unless inbound call recording is also required.");
      setLoader(false);
      return;
    }

    // Validation: Outbound - Cannot have transcription enabled if call recording is disallowed
    if (organization.recording_transcription_config.outbound_transcription !== "disallow" && organization.recording_transcription_config.outbound_recording === "disallow") {
      setErrormsg("Outbound transcription requires call recording to be enabled. Please enable outbound call recording first.");
      setLoader(false);
      return;
    }

    // Validation: Outbound - Cannot have transcription required if call recording is not required
    if (organization.recording_transcription_config.outbound_transcription === "require" && organization.recording_transcription_config.outbound_recording !== "require") {
      setErrormsg("Outbound transcription cannot be required unless outbound call recording is also required.");
      setLoader(false);
      return;
    }

    if (
      organization.outbound_notification_config?.policy &&
      !organization.outbound_notification_config?.channel_id
    ) {
      setErrormsg("Select a notification channel to enable outbound sync.");
      setLoader(false);
      return;
    }

    // Check for affected routes if changing inbound recording policy
    const affectedRoutesResult = await checkAffectedRoutes(organization.recording_transcription_config);
    
    if (affectedRoutesResult.routes.length > 0) {
      // Store the pending update and show modal
      setAffectedRoutes(affectedRoutesResult.routes);
      setRouteConflictAction(affectedRoutesResult.action);
      setPendingOrgUpdate(organization);
      setShowRouteConflictModal(true);
      return;
    }

    // No conflicts, proceed with update
    proceedWithUpdate(organization);
  }

  // Build full payload - same shape we'd send
  function buildFullPayload(orgToUpdate) {
    let full = {
      ...orgToUpdate,
      recording_transcription_config: orgToUpdate.recording_transcription_config,
      outbound_notification_config: normalizeOutboundNotificationConfigForApi(
        orgToUpdate.outbound_notification_config
      ),
    };
    full.twilio_integration = { ...orgToUpdate.twilio_integration };
    if (!full.twilio_integration.api_secret) delete full.twilio_integration.api_secret;
    if (!full.twilio_integration.api_key) delete full.twilio_integration.api_key;
    return full;
  }

  // Original values from org_data (plain format) for diff
  function getOriginalForComparison() {
    const rtc = org_data.recording_transcription_config?.M || {};
    const ti = org_data.twilio_integration?.M || {};
    const outboundNotification = readOutboundNotificationConfig(org_data);
    return {
      orgId: org_data.orgId?.S,
      organization_name: org_data.organization_name?.S,
      org_timezone: org_data.org_timezone?.S,
      recording_transcription_config: {
        inbound_recording: rtc.inbound_recording?.S || "disallow",
        inbound_transcription: rtc.inbound_transcription?.S || "disallow",
        outbound_recording: rtc.outbound_recording?.S || "disallow",
        outbound_transcription: rtc.outbound_transcription?.S || "disallow",
      },
      outbound_notification_config: normalizeOutboundNotificationConfigForApi({
        channel_id: outboundNotification.channel_id,
        policy: outboundNotification.channel_id
          ? outboundNotification.policy || "allow"
          : null,
      }),
      twilio_integration: {
        account_sid: ti.account_sid?.S,
        app_sid: ti?.app_sid?.S,
        api_sid: ti?.api_sid?.S,
        api_secret: ti?.api_secret?.S,
        api_key: ti.api_key?.S,
        transcription_enabled: ti.transcription_enabled?.BOOL ?? false,
        auto_configuration: ti.auto_configuration?.BOOL ?? false,
        primary_phone: ti.primary_phone?.S ?? "",
      },
    };
  }

  function deepEqual(a, b) {
    if (a === b) return true;
    if (typeof a !== "object" || a == null || typeof b !== "object" || b == null) return false;
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    for (const k of keysA) {
      if (!keysB.includes(k)) return false;
      if (!deepEqual(a[k], b[k])) return false;
    }
    return true;
  }

  // Compare twilio_integration excluding api_key (form has decoded value, original has encrypted - never match)
  function twilioIntegrationEqual(a, b) {
    const omit = (obj, key) => {
      const { [key]: _, ...rest } = obj;
      return rest;
    };
    return deepEqual(omit(a, "api_key"), omit(b, "api_key"));
  }

  // Only include changed fields; recording_transcription_config and twilio_integration are all-or-nothing
  function buildDiffPayload(fullPayload, original) {
    const payload = { orgId: fullPayload.orgId };
    if (fullPayload.organization_name !== original.organization_name) payload.organization_name = fullPayload.organization_name;
    if (fullPayload.org_timezone !== original.org_timezone) payload.org_timezone = fullPayload.org_timezone;
    if (!deepEqual(fullPayload.recording_transcription_config, original.recording_transcription_config)) {
      payload.recording_transcription_config = fullPayload.recording_transcription_config;
    }
    if (!deepEqual(fullPayload.outbound_notification_config, original.outbound_notification_config)) {
      payload.outbound_notification_config = fullPayload.outbound_notification_config;
    }
    if (!twilioIntegrationEqual(fullPayload.twilio_integration, original.twilio_integration)) {
      const ti = { ...fullPayload.twilio_integration };
      if (!ti.api_secret) delete ti.api_secret;
      if (!ti.api_key) delete ti.api_key;
      payload.twilio_integration = ti;
    }
    return payload;
  }

  async function proceedWithUpdate(orgToUpdate, updateRoutes = false) {
    setLoader(true);
    
    // Update routes if requested
    if (updateRoutes && affectedRoutes.length > 0 && routeConflictAction) {
      const success = await updateAffectedRoutes(affectedRoutes, routeConflictAction);
      if (!success) {
        setErrormsg("Failed to update some routes. Organization settings were still saved.");
      }
    }
    const fullPayload = buildFullPayload(orgToUpdate);
    const original = getOriginalForComparison();
    const m = buildDiffPayload(fullPayload, original);

    // Nothing changed - still update local state / close modals but skip API call
    if (Object.keys(m).length === 1 && m.orgId) {
      setLoader(false);
      if (showRouteConflictModal) {
        setShowRouteConflictModal(false);
        setAffectedRoutes([]);
        setRouteConflictAction(null);
        setPendingOrgUpdate(null);
      }
      return;
    }
    if (orgToUpdate.twilio_integration.primary_phone.length >= 7) {
      let check = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}get-user-by-id`,
        {
          userId: JSON.parse(localStorage.getItem("user")).userId.S,
        }
      );

      if (check.status === 200) {
        const user___ = check.data.user;

        if (
          user___.relatedOrgs.L?.find(
            (e) =>
              e?.M.org_id?.S ===
              JSON.parse(localStorage.getItem("organization")).orgId.S
          )
        ) {
          try {
            //console.log({m})

            let organization_data = await axios.post(
              process.env.REACT_APP_BACKEND_URL + "update-organization",
              m
            );
            //console.log(organization_data)

            localStorage.setItem(
              "organization",
              JSON.stringify(organization_data.data.organization)
            );

            setLoader(false);
            const updated_org_data = organization_data.data.organization;

            setSuccessMsg("Organization details updated successfully");
    const uti = updated_org_data.twilio_integration?.M;
    setOrganization({
      orgId: updated_org_data.orgId.S,
      organization_name: updated_org_data.organization_name.S,
      org_timezone: updated_org_data.org_timezone.S,
      twilio_integration: {
        account_sid: uti?.account_sid?.S ?? organization.twilio_integration?.account_sid,
        api_key: uti?.api_key?.S ?? "",
        app_sid: uti?.app_sid?.S ?? organization.twilio_integration?.app_sid,
        api_sid: uti?.api_sid?.S ?? organization.twilio_integration?.api_sid,
        api_secret: uti?.api_secret?.S ?? "",
        transcription_enabled: uti?.transcription_enabled?.BOOL ?? organization.twilio_integration?.transcription_enabled,
        auto_configuration: uti?.auto_configuration?.BOOL ?? organization.twilio_integration?.auto_configuration,
        primary_phone: uti?.primary_phone?.S ?? organization.twilio_integration?.primary_phone,
      },
              recording_transcription_config: {
                inbound_recording: updated_org_data.recording_transcription_config?.M?.inbound_recording?.S || "disallow",
                inbound_transcription: updated_org_data.recording_transcription_config?.M?.inbound_transcription?.S || "disallow",
                outbound_recording: updated_org_data.recording_transcription_config?.M?.outbound_recording?.S || "disallow",
                outbound_transcription: updated_org_data.recording_transcription_config?.M?.outbound_transcription?.S || "disallow",
              },
              outbound_notification_config: (() => {
                const { channel_id, policy } =
                  readOutboundNotificationConfig(updated_org_data);
                return {
                  channel_id,
                  policy: channel_id ? policy || "allow" : null,
                };
              })(),
    });

            setTimeout(() => {
              setSuccessMsg(null);
              //window.location.reload()
            }, 3000);
            
            // Close modal if it was open
            if (showRouteConflictModal) {
              setShowRouteConflictModal(false);
              setAffectedRoutes([]);
              setRouteConflictAction(null);
              setPendingOrgUpdate(null);
            }
          } catch (error) {
            if (error.response && error.response.data) {
              setErrormsg(error.response.data.message);
            }
            setLoader(false);
          }
        } else {
          window.location.reload();
        }
      }
    } else {
      setErrormsg("Please add a primary phone number");
      setLoader(false);
    }
  }

  // Current user's role in this org (from localStorage; no API call needed)
  const currentUserType = (() => {
    try {
      const org = JSON.parse(localStorage.getItem("organization"));
      const user = JSON.parse(localStorage.getItem("user"));
      const entry = org?.users?.L?.find((u) => u?.M?.userId?.S === user?.userId?.S);
      return entry?.M?.type?.S ?? null;
    } catch {
      return null;
    }
  })();

  const [search, setSearch] = useState("");

  const [api_key_generated, setApiKeyGenerated] = useState("");

  // const generateApiKey = async () => {
  //   setLoader1(true)
  //  let url =  "https://dev.backend.twilanswer.com/generate-api-key"
  //     let res = await axios.post(url, {"organization_id": JSON.parse(localStorage.getItem("organization")).orgId.S})
  //     .then((res)=> {
  //       console.log(res.data.api_key)
  //       setApiKeyGenerated( res.data.api_key)
  //       setLoader1(false)
  //     })
  //     .catch((err)=> {
  //         console.log(err)
  //     })

  // }

  const [openGenerateApiKeyModal, setOpenGenerateApiKeyModal] = useState(false);

  return (
    <>
      {/* Route Conflict Modal */}
      <Dialog
        open={showRouteConflictModal}
        onClose={() => {
          setShowRouteConflictModal(false);
          setAffectedRoutes([]);
          setRouteConflictAction(null);
          setPendingOrgUpdate(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "14px",
            padding: "28px",
          },
        }}
        BackdropProps={{
          style: { backgroundColor: "rgba(0,0,0,0.35)" },
        }}
      >
        {/* Header */}
        <div className="flex justify-between items-start mb-4">
          <h2 className="text-[22px] font-semibold text-gray-900">
            {routeConflictAction === 'disable' 
              ? 'Routes Will Be Affected' 
              : 'Routes Need to Be Updated'}
          </h2>
          <Close
            className="cursor-pointer text-gray-400"
            onClick={() => {
              setShowRouteConflictModal(false);
              setAffectedRoutes([]);
              setRouteConflictAction(null);
              setPendingOrgUpdate(null);
            }}
          />
        </div>

        {/* Description */}
        <p className="text-gray-600 text-[15px] leading-relaxed mb-4">
          {routeConflictAction === 'disable' ? (
            <>
              Disabling call recording will affect {affectedRoutes.length} route{affectedRoutes.length !== 1 ? 's' : ''} that currently have recording enabled:
            </>
          ) : (
            <>
              Requiring call recording will affect {affectedRoutes.length} route{affectedRoutes.length !== 1 ? 's' : ''} that currently don't have recording enabled:
            </>
          )}
        </p>

        {/* Route List */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4 max-h-[200px] overflow-y-auto">
          <ul className="space-y-2">
            {affectedRoutes.map((route, index) => (
              <li key={index} className="text-[14px] text-gray-700">
                <span className="font-semibold">
                  {route.name?.S || route.name || `Route ${index + 1}`}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Question */}
        <p className="text-gray-600 text-[15px] leading-relaxed mb-6">
          {routeConflictAction === 'disable' ? (
            <>Would you like to automatically disable recording on these routes?</>
          ) : (
            <>Would you like to automatically enable recording on these routes?</>
          )}
        </p>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button
            onClick={() => {
              setShowRouteConflictModal(false);
              setAffectedRoutes([]);
              setRouteConflictAction(null);
              setPendingOrgUpdate(null);
            }}
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              px: 3,
              py: 0.4,
              fontSize: "15px",
              color: "#374151",
              borderColor: "#D1D5DB",
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (pendingOrgUpdate) {
                proceedWithUpdate(pendingOrgUpdate, true);
              }
            }}
            variant="contained"
            color="primary"
            sx={{
              borderRadius: "10px",
              px: 3,
              py: 0.4,
              fontSize: "15px",
            }}
            autoFocus
          >
            {routeConflictAction === 'disable' 
              ? 'Disable Recording & Save' 
              : 'Enable Recording & Save'}
          </Button>
        </div>
      </Dialog>

      <div className="w-full px-3 lg:px-0 py-4 lg:py-8  ">
      <ApiKeyModal
        open={openGenerateApiKeyModal}
        setOpen={setOpenGenerateApiKeyModal}
        hasExistingKey={!!org_data?.ta_api_key?.S}
        onKeyGenerated={(updatedOrg) => {
          if (updatedOrg) {
            setOrgData(updatedOrg);
            localStorage.setItem("organization", JSON.stringify(updatedOrg));
          } else {
            const current = JSON.parse(localStorage.getItem("organization")) || org_data;
            const merged = {
              ...current,
              ta_api_key: { S: "***" },
            };
            setOrgData(merged);
            localStorage.setItem("organization", JSON.stringify(merged));
          }
        }}
      />
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={!!successMsg}
        autoHideDuration={6000}
        onClose={() => setSuccessMsg(null)}
        message={successMsg}
      />
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={!!errorMsg}
        autoHideDuration={6000}
        onClose={() => setErrormsg(null)}
      >
        <Alert onClose={() => setErrormsg(null)} severity="error" sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <div className="text-[14px] lg:text-[17px] font-bold mb-1">
        Organization Settings
      </div>
      <div className="text-[12px] lg:text-[14px] text-[#6B7280]  mb-5">
        Configure your organization's basic information, timezone, and recording policies.
      </div>
      {successMsg && (
        <Alert color="success" sx={{ mb: 4 }}>
          <div className=" text-black-600 text-[14px] ">{successMsg}</div>
        </Alert>
      )}

      {currentUserType && currentUserType !== "user" ? (
        <>
          {loader && (
            <div className="flex justify-center">
              <CircularProgress
                size={"1.2rem"}
                color="primary"
                sx={{ animationDuration: "0.7s" }}
              />
            </div>
          )}

          <div className="lg:text-[14px] text-[13px]   lg:space-y-2 space-y-2 lg:mb-3 mb-5 ">
            <div className="lg:text-[14px] font-semibold text-[13px] text-[#374151]">
              Organization ID
            </div>
            <InputBase
              value={organization.orgId}
              readOnly
              sx={{
                bgcolor: "#F9FAFB",
                width: { lg: "250px", xs: "100%" },
                px: 2,
                fontSize: { lg: "15px", xs: "13px" },
                py: 0.5,
                border: "1px solid #E6E7EB",
                borderRadius: "2px",
              }}
            />
          </div>

          <div className="lg:text-[14px] text-[13px]  lg:space-y-2 space-y-2 lg:mb-3 mb-5 ">
            <div className="lg:text-[14px] font-semibold text-[13px] text-[#374151]">
              Organization Name
            </div>
            <InputBase
              value={organization.organization_name}
              onChange={(e) => {
                setOrganization({
                  ...organization,
                  organization_name: e.target.value,
                });
              }}
              sx={{
                bgcolor: "white",
                width: { lg: "100%", xs: "100%" },
                px: 2,
                fontSize: { lg: "15px", xs: "13px" },
                py: 0.5,
                border: "1px solid #D1D5DB",
                borderRadius: "2px",
              }}
            />
          </div>

          <div className="lg:text-[14px] text-[13px]  lg:space-y-2 space-y-2 lg:mb-3 mb-5">
            <div className="lg:text-[14px] font-semibold text-[13px] text-[#374151]">
              Timezone Settings
            </div>
            <div className="w-full">
              <TimezoneSelect
                value={organization.org_timezone}
                onChange={(e) => {
                  setOrganization({ ...organization, org_timezone: e.value });
                }}
                className="text-[14px]   "
                styles={{
                  control: (provided) => ({
                    ...provided,
                    py: 0.5,
                    backgroundColor: "white",
                    borderColor: "#D1D5DB ",
                    boxShadow: "none",
                    fontSize: "14px",
                    width: "full",
                    "@media (max-width: 1024px)": {
                      fontSize: "12px",
                      width: "full", // Adjust the height for smaller screens
                    },
                    "&:hover": {
                      borderColor: "#D1D5DB",
                    },
                  }),
                  menu: (provided) => ({
                    ...provided,
                    backgroundColor: "#F7F7F8",
                    // Remove border from the dropdown menu
                  }),
                  indicatorSeparator: (provided) => ({
                    display: "none", // Remove the indicator separator
                  }),
                }}
              />
              {/* <Select
               fullWidth
               value={organization.org_timezone}
              onChange={(e) => {
                setOrganization({
                  ...organization,
                org_timezone: e.target.value
                });
              }}
               className="mb-2 bg-[#F7F7F8]"
               sx={{
                 fontSize: { lg: "14px", xs: "13px" },
                 padding: "2px 8px", // Adjust padding here
                 "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                 "& .MuiSelect-select": {
                   padding: "2px 8px", // Adjust padding for the text inside Select
                 },
               }}
             >
              {timezones.map((item,index)=> {
                return(
                  <MenuItem value={item.tzCode} >{item.tzCode}</MenuItem>
                )
              })}
             </Select> */}
            </div>
          </div>

          <div className="border border-[] rounded-[5px] flex items-center py-5 px-3 justify-between">
            <div>
              <div className="text-[12px] lg:text-[13px] font-bold ">
              </div>
              <div className="text-[12px] lg:text-[13px] text-[#6B7280]  ">
                Used to authenticate API requests for this organization.{" "}
                <a
                  href="https://twilanswer.com/docs/api-access/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-primary hover:underline"
                >
                  API docs
                </a>
              </div>
            </div>

            <div>
              <Button
                sx={{
                  textTransform: "none",
                  bgcolor: "white",
                  color: "black",
                  py: "5px",
                  px: "10px",
                  borderRadius: "7px",
                  fontSize: { lg: "14px", xs: "13px" },
                  border: "1px solid #E6E7EB",
                }}
                onClick={() => {
                  setOpenGenerateApiKeyModal(true);
                }}
                disabled={loader1}
              >
                {loader1 ? (
                  <CircularProgress size={"1rem"} sx={{ color: "white" }} />
                ) : (
                 org_data?.ta_api_key?.S ? "Regenerate API Key" : "Generate  API Key"
                )}
              </Button>
            </div>
          </div>

          <Divider orientation="horizontal" flexItem sx={{ mt: 4, mb: 4 }} />

          <div className="text-[12px] lg:text-[15px] font-bold mb-4">
            Call Recording & Transcription Settings
          </div>
          <div className="text-[12px] lg:text-[13px] text-[#6B7280] mb-4">
            Configure recording and transcription policies separately for inbound and outbound calls. Note: Transcription requires call recording to be enabled.
          </div>
          <div className="text-[11px] lg:text-[12px] text-[#6B7280] mb-4 italic">
            Some jurisdictions may have laws about call recording. You're responsible for ensuring you are compliant.
          </div>

          {/* Grid Layout */}
          <div className="border border-gray-200 rounded-lg overflow-hidden mb-5">
            {/* Outbound Section */}
            <div className="grid grid-cols-4 bg-gray-50 border-b border-gray-200">
              <div className="px-4 py-3 text-sm font-semibold text-gray-800 border-r border-gray-200">
                Outbound
              </div>
              <div className="px-4 py-3 text-sm font-semibold text-gray-700 border-r border-gray-200 text-center">
                <Tooltip
                  title="Feature is not available and cannot be enabled"
                  arrow
                >
                  <span className="cursor-help">Disabled</span>
                </Tooltip>
              </div>
              <div className="px-4 py-3 text-sm font-semibold text-gray-700 border-r border-gray-200 text-center">
                <Tooltip
                  title="Feature is optional - users can choose to enable or disable it"
                  arrow
                >
                  <span className="cursor-help">Allowed</span>
                </Tooltip>
              </div>
              <div className="px-4 py-3 text-sm font-semibold text-gray-700 text-center">
                <Tooltip
                  title="Feature is always enabled and cannot be disabled"
                  arrow
                >
                  <span className="cursor-help">Required</span>
                </Tooltip>
              </div>
            </div>

            {/* Outbound Recording Row */}
            <div className="grid grid-cols-4 border-b border-gray-200 bg-white">
              <div className="px-4 py-3 border-r border-gray-200 flex items-center">
                <span className="text-sm text-gray-600 ml-4">Recording</span>
              </div>
              <div className="px-4 py-3 border-r border-gray-200 flex items-center justify-center">
                <Radio
                  checked={organization.recording_transcription_config.outbound_recording === "disallow"}
                  onChange={(e) => {
                    setOrganization({
                      ...organization,
                      recording_transcription_config: {
                        ...organization.recording_transcription_config,
                        outbound_recording: "disallow",
                        outbound_transcription: "disallow",
                      },
                    });
                    setErrormsg(null);
                  }}
                  color="primary"
                />
              </div>
              <div className="px-4 py-3 border-r border-gray-200 flex items-center justify-center">
                <Radio
                  checked={organization.recording_transcription_config.outbound_recording === "allow"}
                  onChange={(e) => {
                    setOrganization({
                      ...organization,
                      recording_transcription_config: {
                        ...organization.recording_transcription_config,
                        outbound_recording: "allow",
                        outbound_transcription: organization.recording_transcription_config.outbound_transcription === "require" ? "allow" : organization.recording_transcription_config.outbound_transcription,
                      },
                    });
                    setErrormsg(null);
                  }}
                  color="primary"
                />
              </div>
              <div className="px-4 py-3 flex items-center justify-center">
                <Radio
                  checked={organization.recording_transcription_config.outbound_recording === "require"}
                  onChange={(e) => {
                    setOrganization({
                      ...organization,
                      recording_transcription_config: {
                        ...organization.recording_transcription_config,
                        outbound_recording: "require",
                      },
                    });
                    setErrormsg(null);
                  }}
                  color="primary"
                />
              </div>
            </div>

            {/* Outbound Transcription Row */}
            <div className="grid grid-cols-4 border-b border-gray-200 bg-white">
              <div className="px-4 py-3 border-r border-gray-200 flex items-center">
                <span className="text-sm text-gray-600 ml-4">Transcription</span>
              </div>
              <div className="px-4 py-3 border-r border-gray-200 flex items-center justify-center">
                {organization.recording_transcription_config.outbound_recording === "disallow" ? (
                  <Tooltip
                    title="Recording must be enabled (Allowed or Required) before transcription can be used. Please change the Outbound Recording setting above."
                    arrow
                  >
                    <span>
                      <Radio
                        checked={organization.recording_transcription_config.outbound_transcription === "disallow"}
                        onChange={(e) => {
                          setOrganization({
                            ...organization,
                            recording_transcription_config: {
                              ...organization.recording_transcription_config,
                              outbound_transcription: "disallow",
                            },
                          });
                          setErrormsg(null);
                        }}
                        disabled={true}
                        color="primary"
                        sx={{
                          "&.Mui-disabled": { color: "#D1D5DB" },
                        }}
                      />
                    </span>
                  </Tooltip>
                ) : (
                  <Radio
                    checked={organization.recording_transcription_config.outbound_transcription === "disallow"}
                    onChange={(e) => {
                      setOrganization({
                        ...organization,
                        recording_transcription_config: {
                          ...organization.recording_transcription_config,
                          outbound_transcription: "disallow",
                        },
                      });
                      setErrormsg(null);
                    }}
                    disabled={false}
                    color="primary"
                    sx={{
                      "&.Mui-disabled": { color: "#D1D5DB" },
                    }}
                  />
                )}
              </div>
              <div className="px-4 py-3 border-r border-gray-200 flex items-center justify-center">
                {organization.recording_transcription_config.outbound_recording === "disallow" ? (
                  <Tooltip
                    title="Recording must be enabled (Allowed or Required) before transcription can be used. Please change the Outbound Recording setting above."
                    arrow
                  >
                    <span>
                      <Radio
                        checked={organization.recording_transcription_config.outbound_transcription === "allow"}
                        onChange={(e) => {
                          setOrganization({
                            ...organization,
                            recording_transcription_config: {
                              ...organization.recording_transcription_config,
                              outbound_transcription: "allow",
                            },
                          });
                          setErrormsg(null);
                        }}
                        disabled={true}
                        color="primary"
                        sx={{
                          "&.Mui-disabled": { color: "#D1D5DB" },
                        }}
                      />
                    </span>
                  </Tooltip>
                ) : (
                  <Radio
                    checked={organization.recording_transcription_config.outbound_transcription === "allow"}
                    onChange={(e) => {
                      setOrganization({
                        ...organization,
                        recording_transcription_config: {
                          ...organization.recording_transcription_config,
                          outbound_transcription: "allow",
                        },
                      });
                      setErrormsg(null);
                    }}
                    disabled={false}
                    color="primary"
                    sx={{
                      "&.Mui-disabled": { color: "#D1D5DB" },
                    }}
                  />
                )}
              </div>
              <div className="px-4 py-3 flex items-center justify-center">
                {organization.recording_transcription_config.outbound_recording !== "require" ? (
                  <Tooltip
                    title="Recording must be set to Required before transcription can be required. Please change the Outbound Recording setting to Required above."
                    arrow
                  >
                    <span>
                      <Radio
                        checked={organization.recording_transcription_config.outbound_transcription === "require"}
                        onChange={(e) => {
                          setOrganization({
                            ...organization,
                            recording_transcription_config: {
                              ...organization.recording_transcription_config,
                              outbound_transcription: "require",
                            },
                          });
                          setErrormsg(null);
                        }}
                        disabled={true}
                        color="primary"
                        sx={{
                          "&.Mui-disabled": { color: "#D1D5DB" },
                        }}
                      />
                    </span>
                  </Tooltip>
                ) : (
                  <Radio
                    checked={organization.recording_transcription_config.outbound_transcription === "require"}
                    onChange={(e) => {
                      setOrganization({
                        ...organization,
                        recording_transcription_config: {
                          ...organization.recording_transcription_config,
                          outbound_transcription: "require",
                        },
                      });
                      setErrormsg(null);
                    }}
                    disabled={false}
                    color="primary"
                    sx={{
                      "&.Mui-disabled": { color: "#D1D5DB" },
                    }}
                  />
                )}
              </div>
            </div>

            {/* Inbound Section */}
            <div className="grid grid-cols-4 bg-gray-50 border-b border-gray-200">
              <div className="px-4 py-3 text-sm font-semibold text-gray-800 border-r border-gray-200">
                Inbound
              </div>
              <div className="px-4 py-3 text-sm font-semibold text-gray-700 border-r border-gray-200 text-center">
                <Tooltip
                  title="Feature is not available and cannot be enabled"
                  arrow
                >
                  <span className="cursor-help">Disabled</span>
                </Tooltip>
              </div>
              <div className="px-4 py-3 text-sm font-semibold text-gray-700 border-r border-gray-200 text-center">
                <Tooltip
                  title="Feature is optional - users can choose to enable or disable it"
                  arrow
                >
                  <span className="cursor-help">Allowed</span>
                </Tooltip>
              </div>
              <div className="px-4 py-3 text-sm font-semibold text-gray-700 text-center">
                <Tooltip
                  title="Feature is always enabled and cannot be disabled"
                  arrow
                >
                  <span className="cursor-help">Required</span>
                </Tooltip>
              </div>
            </div>

            {/* Inbound Recording Row */}
            <div className="grid grid-cols-4 border-b border-gray-200 bg-white">
              <div className="px-4 py-3 border-r border-gray-200 flex items-center">
                <span className="text-sm text-gray-600 ml-4">Recording</span>
              </div>
              <div className="px-4 py-3 border-r border-gray-200 flex items-center justify-center">
                <Radio
                  checked={organization.recording_transcription_config.inbound_recording === "disallow"}
                  onChange={(e) => {
                    setOrganization({
                      ...organization,
                      recording_transcription_config: {
                        ...organization.recording_transcription_config,
                        inbound_recording: "disallow",
                        inbound_transcription: "disallow",
                      },
                    });
                    setErrormsg(null);
                  }}
                  color="primary"
                />
              </div>
              <div className="px-4 py-3 border-r border-gray-200 flex items-center justify-center">
                <Radio
                  checked={organization.recording_transcription_config.inbound_recording === "allow"}
                  onChange={(e) => {
                    setOrganization({
                      ...organization,
                      recording_transcription_config: {
                        ...organization.recording_transcription_config,
                        inbound_recording: "allow",
                        inbound_transcription: organization.recording_transcription_config.inbound_transcription === "require" ? "allow" : organization.recording_transcription_config.inbound_transcription,
                      },
                    });
                    setErrormsg(null);
                  }}
                  color="primary"
                />
              </div>
              <div className="px-4 py-3 flex items-center justify-center">
                <Radio
                  checked={organization.recording_transcription_config.inbound_recording === "require"}
                  onChange={(e) => {
                    setOrganization({
                      ...organization,
                      recording_transcription_config: {
                        ...organization.recording_transcription_config,
                        inbound_recording: "require",
                      },
                    });
                    setErrormsg(null);
                  }}
                  color="primary"
                />
              </div>
            </div>

            {/* Inbound Transcription Row */}
            <div className="grid grid-cols-4 bg-white">
              <div className="px-4 py-3 border-r border-gray-200 flex items-center">
                <span className="text-sm text-gray-600 ml-4">Transcription</span>
              </div>
              <div className="px-4 py-3 border-r border-gray-200 flex items-center justify-center">
                {organization.recording_transcription_config.inbound_recording === "disallow" ? (
                  <Tooltip
                    title="Recording must be enabled (Allowed or Required) before transcription can be used. Please change the Inbound Recording setting above."
                    arrow
                  >
                    <span>
                      <Radio
                        checked={organization.recording_transcription_config.inbound_transcription === "disallow"}
                        onChange={(e) => {
                          setOrganization({
                            ...organization,
                            recording_transcription_config: {
                              ...organization.recording_transcription_config,
                              inbound_transcription: "disallow",
                            },
                          });
                          setErrormsg(null);
                        }}
                        disabled={true}
                        color="primary"
                        sx={{
                          "&.Mui-disabled": { color: "#D1D5DB" },
                        }}
                      />
                    </span>
                  </Tooltip>
                ) : (
                  <Radio
                    checked={organization.recording_transcription_config.inbound_transcription === "disallow"}
                    onChange={(e) => {
                      setOrganization({
                        ...organization,
                        recording_transcription_config: {
                          ...organization.recording_transcription_config,
                          inbound_transcription: "disallow",
                        },
                      });
                      setErrormsg(null);
                    }}
                    disabled={false}
                    color="primary"
                    sx={{
                      "&.Mui-disabled": { color: "#D1D5DB" },
                    }}
                  />
                )}
              </div>
              <div className="px-4 py-3 border-r border-gray-200 flex items-center justify-center">
                {organization.recording_transcription_config.inbound_recording === "disallow" ? (
                  <Tooltip
                    title="Recording must be enabled (Allowed or Required) before transcription can be used. Please change the Inbound Recording setting above."
                    arrow
                  >
                    <span>
                      <Radio
                        checked={organization.recording_transcription_config.inbound_transcription === "allow"}
                        onChange={(e) => {
                          setOrganization({
                            ...organization,
                            recording_transcription_config: {
                              ...organization.recording_transcription_config,
                              inbound_transcription: "allow",
                            },
                          });
                          setErrormsg(null);
                        }}
                        disabled={true}
                        color="primary"
                        sx={{
                          "&.Mui-disabled": { color: "#D1D5DB" },
                        }}
                      />
                    </span>
                  </Tooltip>
                ) : (
                  <Radio
                    checked={organization.recording_transcription_config.inbound_transcription === "allow"}
                    onChange={(e) => {
                      setOrganization({
                        ...organization,
                        recording_transcription_config: {
                          ...organization.recording_transcription_config,
                          inbound_transcription: "allow",
                        },
                      });
                      setErrormsg(null);
                    }}
                    disabled={false}
                    color="primary"
                    sx={{
                      "&.Mui-disabled": { color: "#D1D5DB" },
                    }}
                  />
                )}
              </div>
              <div className="px-4 py-3 flex items-center justify-center">
                {organization.recording_transcription_config.inbound_recording !== "require" ? (
                  <Tooltip
                    title="Recording must be set to Required before transcription can be required. Please change the Inbound Recording setting to Required above."
                    arrow
                  >
                    <span>
                      <Radio
                        checked={organization.recording_transcription_config.inbound_transcription === "require"}
                        onChange={(e) => {
                          setOrganization({
                            ...organization,
                            recording_transcription_config: {
                              ...organization.recording_transcription_config,
                              inbound_transcription: "require",
                            },
                          });
                          setErrormsg(null);
                        }}
                        disabled={true}
                        color="primary"
                        sx={{
                          "&.Mui-disabled": { color: "#D1D5DB" },
                        }}
                      />
                    </span>
                  </Tooltip>
                ) : (
                  <Radio
                    checked={organization.recording_transcription_config.inbound_transcription === "require"}
                    onChange={(e) => {
                      setOrganization({
                        ...organization,
                        recording_transcription_config: {
                          ...organization.recording_transcription_config,
                          inbound_transcription: "require",
                        },
                      });
                      setErrormsg(null);
                    }}
                    disabled={false}
                    color="primary"
                    sx={{
                      "&.Mui-disabled": { color: "#D1D5DB" },
                    }}
                  />
                )}
              </div>
            </div>
          </div>

          <div className="text-[12px] lg:text-[15px] font-bold mb-4 mt-6">
            Outbound Call Notifications
          </div>
          <div className="text-[12px] lg:text-[13px] text-[#6B7280] mb-4">
            Sync outbound dialer calls to a notification channel. Users can toggle
            notifications per call when the policy is Optional.
          </div>

          <div className="mb-4">
            <div className="text-[12px] lg:text-[13px] font-semibold mb-2">
              Outbound notification channel
            </div>
            <RouteSelect
              value={organization.outbound_notification_config?.channel_id ?? ""}
              onChange={(channelId) => {
                setOrganization({
                  ...organization,
                  outbound_notification_config: channelId
                    ? {
                        channel_id: channelId,
                        policy:
                          organization.outbound_notification_config?.policy ||
                          "allow",
                      }
                    : { channel_id: null, policy: null },
                });
                setErrormsg(null);
              }}
              options={notificationChannelOptions}
              placeholder="None"
              addOption={{
                label: "Add Channel",
                onClick: () => {
                  window.location.assign("/dashboard/channels");
                },
              }}
            />
            {!organization.outbound_notification_config?.channel_id && (
              <div className="text-[11px] lg:text-[12px] text-[#6B7280] mt-2">
                Select a channel to sync outbound calls to.
              </div>
            )}
          </div>

          <div className="mb-5">
            <div className="text-[12px] lg:text-[13px] font-semibold mb-2">
              Outbound notification policy
            </div>
            {!organization.outbound_notification_config?.channel_id ? (
              <Tooltip
                title="Select a notification channel before choosing a policy."
                arrow
              >
                <div className="flex items-center space-x-6 opacity-50">
                  <label className="flex items-center space-x-2 cursor-not-allowed">
                    <Radio checked={false} disabled color="primary" />
                    <span className="text-sm text-gray-600">Optional</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-not-allowed">
                    <Radio checked={false} disabled color="primary" />
                    <span className="text-sm text-gray-600">Required</span>
                  </label>
                </div>
              </Tooltip>
            ) : (
              <div className="flex items-center space-x-6">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <Radio
                    checked={
                      organization.outbound_notification_config.policy ===
                      "allow"
                    }
                    onChange={() => {
                      setOrganization({
                        ...organization,
                        outbound_notification_config: {
                          ...organization.outbound_notification_config,
                          policy: "allow",
                        },
                      });
                      setErrormsg(null);
                    }}
                    color="primary"
                  />
                  <span className="text-sm text-gray-600">Optional</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <Radio
                    checked={
                      organization.outbound_notification_config.policy ===
                      "require"
                    }
                    onChange={() => {
                      setOrganization({
                        ...organization,
                        outbound_notification_config: {
                          ...organization.outbound_notification_config,
                          policy: "require",
                        },
                      });
                      setErrormsg(null);
                    }}
                    color="primary"
                  />
                  <span className="text-sm text-gray-600">Required</span>
                </label>
              </div>
            )}
          </div>

          <Divider orientation="horizontal" flexItem sx={{ mt: 4, mb: 4 }} />

          <div className="flex justify-end pt-5 mt-4">
            <Button
              variant="contained"
                color="primary"
                sx={{
                py: { lg: "10px", xs: "5px" },
                px: "20px",
                borderRadius: "12px",
                fontSize: { lg: "14px", xs: "12px" },
                height: "fit",
              }}
              onClick={updateOrganization}
              disabled={loader1 && loader}
            >
              {loader ? (
                <CircularProgress size={"1rem"} sx={{ color: "white" }} />
              ) : (
                "  Save Changes"
              )}
            </Button>
          </div>
        </>
      ) : (
        <Alert color="warning">
          <div className=" text-black-600 text-[14px] ">
            You can't view this page in this organization because your type is
            user
          </div>
        </Alert>
      )}
    </div>
    </>
  );
};

export default Organization;
